import { Component, OnInit, NgZone } from '@angular/core';
import { NavService } from '../../service/nav.service';
import { Router } from '@angular/router';
import { APIService } from '../../service/apiService.service';
import { ModeService, ModeOption } from '../../service/mode.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit {
  public open: boolean = false;
  public openNav: boolean = false;
  public isOpenMobile: boolean;
  public profile: any;
  public availableProfiles: ModeOption[] = [];
  public activeMode: string = 'user';

  constructor(
    public navServices: NavService,
    public route: Router,
    private ngZone: NgZone,
    private apiService: APIService,
    public modeService: ModeService,
  ) {
    this.profile = JSON.parse(localStorage.getItem('currentUser') || '{}');
    this.availableProfiles = this.modeService.getAvailableProfiles();
    this.activeMode = this.modeService.activeMode;
    this.modeService.activeMode$.subscribe(mode => this.activeMode = mode);
  }

  ngOnInit() {
    // Cargar el perfil actualizado desde el backend con la nueva API
    this.loadProfile();
  }

  loadProfile() {
    this.apiService.getProfile().subscribe({
      next: (data: any) => {
        if (data) {
          // Intentar obtener la imagen en este orden de prioridad:
          // 1. profileFileUrl (si es una URL válida)
          // 2. profileImage.base64 (si existe)
          // 3. imgProfile (fallback)
          let imageUrl = data.profileFileUrl || data.imgProfile || null;
          
          // Limpiar la URL si es una ruta del servidor
          imageUrl = this.cleanImageUrl(imageUrl);
          
          // Si no hay URL válida pero hay base64, usar la imagen base64
          if (!imageUrl && data.profileImage?.base64) {
            imageUrl = `data:image/jpeg;base64,${data.profileImage.base64}`;
          }
          
          // Actualizar el perfil en memoria (con imagen base64)
          this.profile = {
            ...this.profile,
            ...data,
            imgProfile: imageUrl,
            profileFileUrl: imageUrl // Mantener ambas propiedades para compatibilidad
          };
          
          // Guardar en localStorage PRESERVANDO datos existentes como token
          try {
            const existingData = JSON.parse(localStorage.getItem('currentUser') || '{}');
            
            const userDataForStorage = {
              ...existingData, // Preservar token y otros datos existentes
              ...data, // Actualizar con nuevos datos del perfil
              // NO incluir el base64 completo en profileImage
              profileFileUrl: data.profileFileUrl,
              profileImage: data.profileImage ? {
                hasBase64: !!data.profileImage.base64
              } : null
            };
            
            // Remover imgProfile si es base64 para evitar QuotaExceededError
            if (userDataForStorage.imgProfile?.startsWith('data:')) {
              delete userDataForStorage.imgProfile;
            }
            
            localStorage.setItem('currentUser', JSON.stringify(userDataForStorage));
            
            // Guardar la imagen base64 en una clave SEPARADA de sessionStorage (se limpia al cerrar navegador)
            if (imageUrl?.startsWith('data:')) {
              try {
                sessionStorage.setItem('profileImageBase64', imageUrl);
              } catch (e) {
                console.warn('⚠️ [HEADER] No se pudo guardar imagen en sessionStorage:', e);
              }
            }
            
            // Disparar evento de storage para que otros componentes se enteren del cambio
            window.dispatchEvent(new StorageEvent('storage', {
              key: 'currentUser',
              newValue: JSON.stringify(userDataForStorage),
              url: window.location.href
            }));
          } catch (e) {
            console.error('❌ [HEADER] Error al actualizar localStorage:', e);
          }
        }
      },
      error: (err) => {
        console.error('❌ [HEADER] Error al cargar perfil:', err);
      }
    });
  }

  // Limpiar URL de imagen del servidor a URL pública
  private cleanImageUrl(url: any): string | null {
    if (!url || typeof url !== 'string') return null;
    
    // Si es solo un número, retornar null
    if (/^\d+(\.\d+)?$/.test(url)) return null;
    
    // Si es una ruta del servidor, retornar null (sin warning)
    if (url.startsWith('/') && (url.includes('/var/') || url.includes('/usr/') || url.includes('/home/') || url.includes('/opt/'))) {
      return null;
    }
    
    // Si ya es una URL válida, retornarla
    if (url.startsWith('http') || url.startsWith('https') || url.startsWith('assets') || url.startsWith('data:image')) {
      return url;
    }
    
    return null;
  }

  // Validar que la URL de la imagen sea válida (no un número o valor inválido)
  isValidImageUrl(url: any): boolean {
    if (!url) return false;
    
    // Verificar que sea un string
    if (typeof url !== 'string') return false;
    
    // Verificar que no sea solo un número
    if (/^\d+(\.\d+)?$/.test(url)) return false;
    
    // Rechazar rutas del servidor (comienzan con / y contienen /var, /usr, /home, etc.)
    if (url.startsWith('/') && (url.includes('/var/') || url.includes('/usr/') || url.includes('/home/') || url.includes('/opt/'))) {
      return false;
    }
    
    // Verificar que comience con http, https, assets, o data:image (base64)
    if (url.startsWith('http') || url.startsWith('https') || url.startsWith('assets') || url.startsWith('data:image')) {
      return true;
    }
    
    return false;
  }

  switchMode(mode: string): void {
    this.modeService.setMode(mode);
  }

  logOut() {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('activeMode');
    this.route.navigateByUrl("/auth/sign-in");
  }

  // Manejar error de carga de imagen
  onImageError(event: any): void {
    // Reemplazar con imagen por defecto silenciosamente
    event.target.src = 'assets/images/dashboard/man.png';
  }

  collapseSidebar() {
    this.open = !this.open;
    this.navServices.collapseSidebar = !this.navServices.collapseSidebar;

    // Ejecutar fuera de Angular y en el próximo frame para minimizar jank
    this.ngZone.runOutsideAngular(() => {
      const apply = () => {
        try {
          if (this.navServices.collapseSidebar) {
            document.body.classList.add('sidebar-collapsed');
          } else {
            document.body.classList.remove('sidebar-collapsed');
          }
        } catch {}
      };
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => apply());
      } else {
        setTimeout(apply, 0);
      }
    });
}

  openMobileNav() {
    this.openNav = !this.openNav;
  }

}
